import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { Heading } from "./heading";
import { Text } from "./text";
import {
  PaperAirplaneIcon,
  ClockIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

// Tarifa del modelo del bot (gemini-3.5-flash-lite, ago 2026): USD por millón
// de tokens. Si cambias TELEGRAM_LLM_MODEL en el backend, actualiza aquí.
const LLM_USD_PER_M_INPUT = 0.3;
const LLM_USD_PER_M_OUTPUT = 2.5;

// Espejo del cap del backend (env TELEGRAM_OWNERS_VENUE_DAILY_LIMIT) — si lo
// cambias allá, actualiza aquí para que "usado / límite" cuadre.
const VENUE_DAILY_LIMIT = 300;
const VENUE_WINDOW_MS = 24 * 60 * 60 * 1000;

const fmt = (n) => (n || 0).toLocaleString("es-MX");

const costoUsd = (tokensIn, tokensOut) =>
  (tokensIn / 1e6) * LLM_USD_PER_M_INPUT +
  (tokensOut / 1e6) * LLM_USD_PER_M_OUTPUT;

const fmtUsd = (usd) =>
  usd >= 0.01 || usd === 0 ? `$${usd.toFixed(2)}` : `$${usd.toFixed(4)}`;

const fmtMxn = (usd, tc) => {
  const mxn = usd * tc;
  return mxn >= 0.01 || mxn === 0
    ? `$${mxn.toFixed(2)} MXN`
    : `$${mxn.toFixed(4)} MXN`;
};

// Las métricas se bucketean por día de America/Mexico_City en el backend;
// aquí usamos la fecha local del admin como aproximación suficiente.
const dayKey = (date) =>
  `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(
    date.getDate(),
  ).padStart(2, "0")}`;

const emptyAgg = () => ({
  total: 0,
  hoy: 0,
  nl: 0,
  comandos: 0,
  tokensIn: 0,
  tokensOut: 0,
});

const acumular = (agg, data, hoyKey) => {
  const total = data.total || 0;
  const nl = data.nl || 0;
  agg.total += total;
  agg.nl += nl;
  agg.comandos += total - nl;
  agg.tokensIn += data.llmInputTokens || 0;
  agg.tokensOut += data.llmOutputTokens || 0;
  if (data.date === hoyKey) agg.hoy += total;
};

const TC_STORAGE_KEY = "venueboard_tc_usd_mxn";

const AdminTelegramBoard = () => {
  const [rows, setRows] = useState([]);
  const [globalAgg, setGlobalAgg] = useState(emptyAgg());
  // Tipo de cambio USD→MXN manual (persistido localmente por admin).
  const [tipoCambio, setTipoCambio] = useState(() => {
    const stored = parseFloat(localStorage.getItem(TC_STORAGE_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : 18.5;
  });
  const [loading, setLoading] = useState(true);
  const [venueSel, setVenueSel] = useState(null); // { id, name }
  const [chats, setChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [chatSel, setChatSel] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [mensajesLoading, setMensajesLoading] = useState(false);

  // Uso de los últimos 7 días desde telegramUsage (agregado por venue).
  useEffect(() => {
    const load = async () => {
      try {
        const cutoff = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000);
        const hoyKey = dayKey(new Date());
        const snap = await getDocs(
          query(
            collection(db, "telegramUsage"),
            where("date", ">=", dayKey(cutoff)),
          ),
        );
        const porVenue = {};
        const global = emptyAgg();
        snap.docs.forEach((d) => {
          const data = d.data();
          if (!data.venueId) return;
          if (data.venueId === "global") {
            acumular(global, data, hoyKey);
            return;
          }
          if (!porVenue[data.venueId]) porVenue[data.venueId] = emptyAgg();
          acumular(porVenue[data.venueId], data, hoyKey);
        });
        const ids = Object.keys(porVenue);
        const withNames = await Promise.all(
          ids.map(async (id) => {
            let name = id;
            try {
              const v = await getDoc(doc(db, "venues", id));
              if (v.exists()) name = v.data().name || id;
            } catch {
              // sin nombre, se muestra el id
            }
            // Uso de la ventana rodante de 24h contra el cap diario (counter
            // doc del backend). Ventana expirada = contador en cero.
            let usadoVentana = 0;
            try {
              const lim = await getDoc(
                doc(db, "telegramUsageLimits", `venue_${id}`),
              );
              if (lim.exists()) {
                const data = lim.data();
                const ws = data.windowStart?.toDate?.();
                if (ws && Date.now() - ws.getTime() < VENUE_WINDOW_MS) {
                  usadoVentana = data.count || 0;
                }
              }
            } catch {
              // sin doc de límite = sin uso en la ventana
            }
            return { id, name, usadoVentana, ...porVenue[id] };
          }),
        );
        withNames.sort((a, b) => b.total - a.total);
        setRows(withNames);
        setGlobalAgg(global);
      } catch (err) {
        console.error("Error loading telegram usage:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Grupos vinculados del venue seleccionado.
  useEffect(() => {
    if (!venueSel) {
      setChats([]);
      setChatSel(null);
      return;
    }
    setChatsLoading(true);
    const load = async () => {
      try {
        const snap = await getDocs(
          collection(db, "venues", venueSel.id, "telegramChats"),
        );
        setChats(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error loading telegram chats:", err);
        setChats([]);
      } finally {
        setChatsLoading(false);
      }
    };
    load();
  }, [venueSel]);

  // Conversación del grupo seleccionado (en vivo).
  useEffect(() => {
    if (!venueSel || !chatSel) {
      setMensajes([]);
      return;
    }
    setMensajesLoading(true);
    const q = query(
      collection(
        db,
        "venues",
        venueSel.id,
        "telegramChats",
        chatSel.id,
        "mensajes",
      ),
      orderBy("at", "desc"),
      limit(50),
    );
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setMensajes(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setMensajesLoading(false);
      },
      (err) => {
        console.error("Mensajes telegram snapshot error:", err);
        setMensajesLoading(false);
      },
    );
    return unsub;
  }, [venueSel, chatSel]);

  const tiles = [
    { label: "Mensajes hoy", value: fmt(globalAgg.hoy) },
    { label: "Mensajes 7 días", value: fmt(globalAgg.total) },
    {
      label: "NL vs comandos (7d)",
      value: `${fmt(globalAgg.nl)} / ${fmt(globalAgg.comandos)}`,
    },
    {
      label: "Tokens LLM 7d (in/out)",
      value: `${fmt(globalAgg.tokensIn)} / ${fmt(globalAgg.tokensOut)}`,
    },
    {
      label: "Costo LLM 7d (est. USD)",
      value: fmtUsd(costoUsd(globalAgg.tokensIn, globalAgg.tokensOut)),
      sub: fmtMxn(costoUsd(globalAgg.tokensIn, globalAgg.tokensOut), tipoCambio),
    },
  ];

  const handleTipoCambio = (e) => {
    const value = parseFloat(e.target.value);
    if (Number.isFinite(value) && value > 0) {
      setTipoCambio(value);
      localStorage.setItem(TC_STORAGE_KEY, String(value));
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-1">
        <PaperAirplaneIcon className="h-6 w-6" style={{ color: ACCENT }} />
        <Heading level={1}>Bot de Telegram</Heading>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <Text>
          Uso del bot por venue (últimos 7 días) y conversaciones de cada
          grupo.
        </Text>
        <label className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
          Tipo de cambio USD→MXN
          <input
            type="number"
            min="1"
            step="0.05"
            defaultValue={tipoCambio}
            onChange={handleTipoCambio}
            className="w-20 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
          />
        </label>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {tiles.map((t) => (
          <div
            key={t.label}
            className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-4 shadow-sm"
          >
            <div className="text-xs text-zinc-500 dark:text-zinc-400">
              {t.label}
            </div>
            <div className="text-xl font-semibold text-zinc-900 dark:text-white mt-1">
              {t.value}
            </div>
            {t.sub && (
              <div className="text-xs mt-0.5" style={{ color: ACCENT }}>
                ≈ {t.sub}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden mb-6">
        {loading ? (
          <div className="text-center py-10 text-zinc-400">Cargando...</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-sm text-zinc-400">
            Sin uso registrado en los últimos 7 días.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 dark:border-zinc-700/60">
                  {[
                    "Venue",
                    "Hoy",
                    "7 días",
                    "NL",
                    "Comandos",
                    "Tokens (in/out)",
                    "Costo 7d (USD)",
                    "Límite 24h",
                    "",
                  ].map((h, i) => (
                    <th
                      key={i}
                      className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-zinc-500 ${
                        i === 0 ? "text-left" : "text-right"
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className={
                      venueSel?.id === r.id
                        ? "bg-zinc-50 dark:bg-zinc-700/20"
                        : ""
                    }
                  >
                    <td className="px-4 py-2.5 text-zinc-700 dark:text-zinc-200 font-medium">
                      {r.name}
                    </td>
                    <td className="px-4 py-2.5 text-right">{fmt(r.hoy)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold">
                      {fmt(r.total)}
                    </td>
                    <td className="px-4 py-2.5 text-right">{fmt(r.nl)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {fmt(r.comandos)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-zinc-500">
                      {fmt(r.tokensIn)} / {fmt(r.tokensOut)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="font-semibold" style={{ color: ACCENT }}>
                        {fmtUsd(costoUsd(r.tokensIn, r.tokensOut))}
                      </div>
                      <div className="text-[11px] text-zinc-400">
                        ≈ {fmtMxn(costoUsd(r.tokensIn, r.tokensOut), tipoCambio)}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div
                        className={
                          r.usadoVentana >= VENUE_DAILY_LIMIT
                            ? "font-semibold text-red-600"
                            : r.usadoVentana >= VENUE_DAILY_LIMIT * 0.8
                              ? "font-semibold text-amber-600"
                              : ""
                        }
                      >
                        {fmt(r.usadoVentana)} / {fmt(VENUE_DAILY_LIMIT)}
                      </div>
                      <div className="text-[11px] text-zinc-400">
                        quedan{" "}
                        {fmt(Math.max(0, VENUE_DAILY_LIMIT - r.usadoVentana))}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() =>
                          setVenueSel(
                            venueSel?.id === r.id
                              ? null
                              : { id: r.id, name: r.name },
                          )
                        }
                        className="text-xs font-medium underline"
                        style={{ color: ACCENT }}
                      >
                        {venueSel?.id === r.id ? "Cerrar" : "Ver chats"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {venueSel && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-4 shadow-sm mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
              Grupos de {venueSel.name}
            </h3>
            <button
              type="button"
              onClick={() => setVenueSel(null)}
              className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 p-1"
              aria-label="Cerrar grupos"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
          {chatsLoading ? (
            <div className="text-center py-6 text-zinc-400 text-sm">
              Cargando...
            </div>
          ) : chats.length === 0 ? (
            <div className="text-center py-6 text-sm text-zinc-400">
              Este venue no tiene grupos vinculados.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {chats.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() =>
                    setChatSel(chatSel?.id === c.id ? null : c)
                  }
                  className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition ${
                    chatSel?.id === c.id
                      ? "border-transparent text-white"
                      : "border-zinc-200 dark:border-zinc-700/60 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-700/30"
                  } ${c.active ? "" : "opacity-50"}`}
                  style={
                    chatSel?.id === c.id
                      ? { backgroundColor: ACCENT }
                      : undefined
                  }
                >
                  <ClockIcon className="h-4 w-4" />
                  {c.title || `Grupo ${c.id}`}
                  {!c.active && " (inactivo)"}
                </button>
              ))}
            </div>
          )}

          {chatSel && (
            <div className="mt-4">
              {mensajesLoading ? (
                <div className="text-center py-6 text-zinc-400 text-sm">
                  Cargando conversación...
                </div>
              ) : mensajes.length === 0 ? (
                <div className="text-center py-6 text-sm text-zinc-400">
                  Este grupo todavía no le ha preguntado nada al bot.
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto space-y-3">
                  {mensajes.map((m) => (
                    <div
                      key={m.id}
                      className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 p-3"
                    >
                      <div className="flex items-center gap-2 text-[11px] text-zinc-400 mb-1.5">
                        <span className="font-medium text-zinc-500 dark:text-zinc-300">
                          {m.autor || "Alguien del grupo"}
                        </span>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                            m.tipo === "comando"
                              ? "bg-zinc-100 dark:bg-zinc-700/60 text-zinc-500 dark:text-zinc-300"
                              : "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                          }`}
                        >
                          {m.tipo === "comando" ? "comando" : "pregunta"}
                        </span>
                        <span className="ml-auto">
                          {m.at?.toDate
                            ? m.at.toDate().toLocaleString("es-MX", {
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : ""}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-zinc-800 dark:text-zinc-100 mb-1">
                        {m.pregunta}
                      </p>
                      <p className="text-sm text-zinc-600 dark:text-zinc-300 whitespace-pre-wrap">
                        {m.respuesta}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminTelegramBoard;
